import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, ShieldCheck, Zap, Lock, Loader2, Mail } from "lucide-react";
import { SEO } from "@/components/SEO";
import { isNativeApp } from "@/lib/platform";
import { useGoogleSignIn } from "@/hooks/useGoogleSignIn";
import { useEmailAuth } from "@/hooks/useEmailAuth";
import { useToast } from "@/hooks/use-toast";
import NativeWelcomeAuth from "@/pages/auth/NativeWelcomeAuth";

// Native OAuth now uses the web /~oauth catch-all instead of a custom scheme.
// The deep-link infrastructure (fitauraai://) remains in AndroidManifest.xml
// and AuthContext as a backup for future cross-app handoffs.

const HEADLINE_FONT = "'Space Grotesk', sans-serif";
const BODY_FONT = "'Manrope', sans-serif";

const Auth = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const reduce = useReducedMotion();
  const { signingIn, signInWithGoogle: google } = useGoogleSignIn();
  const { busy: emailBusy, signIn, resendConfirmation, sendPasswordReset } = useEmailAuth();
  const { toast } = useToast();
  // Email signups are temporarily disabled — only existing users can sign in.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [signedUpEmail, setSignedUpEmail] = useState("");

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: "Missing details", description: "Enter your email and password.", variant: "destructive" });
      return;
    }
    {
      const res = await signIn(email, password);
      if (res.ok === false) {
        if (res.code === "email_not_confirmed") {
          setSignedUpEmail(email);
          setNeedsConfirm(true);
          toast({ title: "Verify your email", description: "We sent a verification link — check your inbox." });
          return;
        }
        toast({ title: "Sign in failed", description: res.message, variant: "destructive" });
      }
    }
  };

  const handleForgot = async () => {
    if (!email) {
      toast({ title: "Enter your email", description: "Type your email above first, then tap Forgot.", variant: "destructive" });
      return;
    }
    await sendPasswordReset(email);
  };

  // Detect that we landed on /auth WHILE OAuth tokens are inbound (browser
  // navigated back here with a hash/code instead of going to /~oauth). Show
  // a friendly "Finishing sign-in…" state instead of the form so the user
  // doesn't see the auth UI flicker for a fraction of a second.
  const incomingTokens = useMemo(() => {
    if (typeof window === "undefined") return false;
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const hashParams = new URLSearchParams(hash);
    const queryParams = new URLSearchParams(window.location.search);
    return Boolean(
      hashParams.get("access_token") ||
        queryParams.get("access_token") ||
        queryParams.get("code"),
    );
  }, []);

  useEffect(() => {
    const fontId = "stitch-fonts";
    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&family=Manrope:wght@400;500;700;800&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (session) navigate("/app/style", { replace: true });
  }, [session, navigate]);

  // On native Android, swap the entire web layout for the Stitch-designed
  // mobile Welcome screen. Web/desktop UI is untouched.
  if (isNativeApp()) {
    return <NativeWelcomeAuth />;
  }

  const fadeUp = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        animate: { opacity: 1, y: 0 },
      };

  if (incomingTokens) {
    return (
      <>
        <SEO />
        <div
          className="dark flex min-h-screen flex-col items-center justify-center gap-3 bg-[#0e0e11] px-6 text-[#e7e4ec]"
          style={{ fontFamily: BODY_FONT }}
        >
          <Loader2 className="h-7 w-7 animate-spin text-[#ac8aff]" />
          <p
            className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#bfa5ff]"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            Finishing Google sign-in…
          </p>
          <p className="max-w-xs text-center text-xs text-[#76757b]">
            One moment while we secure your runway pass.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO />
    <div
      className="dark relative flex min-h-screen flex-col bg-[#0e0e11] text-[#e7e4ec] selection:bg-[#ac8aff]/30 selection:text-white"
      style={{ fontFamily: BODY_FONT }}
    >
      {/* Ambient glow blobs to match landing */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-40 top-0 h-[480px] w-[480px] rounded-full bg-[#ac8aff]/15 blur-[140px]" />
        <div className="absolute -right-40 bottom-0 h-[480px] w-[480px] rounded-full bg-[#00daf3]/10 blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #ac8aff 1px, transparent 1px), linear-gradient(to bottom, #ac8aff 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      {/* Top bar */}
      <header className="relative z-10 border-b border-white/5 bg-[#0e0e11]/80 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg shadow-[0_0_24px_-4px_#ac8aff]">
              <img
                src="/app-icon-128.png"
                alt="FitAura AI logo"
                className="h-full w-full object-cover"
                width={36}
                height={36}
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </span>
            <span
              className="text-lg font-bold uppercase tracking-[0.18em] text-white"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              FitAura AI
            </span>
          </Link>
          <Link
            to="/"
            className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#acaab1] transition-colors hover:text-white"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            ← Back to Runway
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="relative z-10 flex flex-1 items-center justify-center px-5 py-14 md:py-20">
        <div className="w-full max-w-md">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center"
          >
            <span
              className="inline-flex items-center gap-2 rounded-full border border-[#ac8aff]/30 bg-[#ac8aff]/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#bfa5ff]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#ac8aff]" />
              Enter the Runway
            </span>
            <h1
              className="mt-5 text-4xl font-bold uppercase leading-[0.95] tracking-tight text-white md:text-5xl"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              Welcome to{" "}
              <span className="bg-gradient-to-r from-[#ac8aff] to-[#00daf3] bg-clip-text text-transparent">
                FitAura
              </span>
            </h1>
            <p className="mx-auto mt-3 max-w-sm text-sm text-[#acaab1]">
              Sign in with Google to start trying outfits in seconds. Your first 3 try-ons are on us.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="relative mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl shadow-[0_0_60px_-15px_#ac8aff]"
          >
            {/* Corner brackets */}
            <div className="pointer-events-none absolute left-2 top-2 h-4 w-4 border-l-2 border-t-2 border-[#ac8aff]" />
            <div className="pointer-events-none absolute right-2 top-2 h-4 w-4 border-r-2 border-t-2 border-[#ac8aff]" />
            <div className="pointer-events-none absolute bottom-2 left-2 h-4 w-4 border-b-2 border-l-2 border-[#ac8aff]" />
            <div className="pointer-events-none absolute bottom-2 right-2 h-4 w-4 border-b-2 border-r-2 border-[#ac8aff]" />

            <Button
              type="button"
              onClick={google}
              disabled={signingIn}
              className="group h-12 w-full rounded-full bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] text-xs font-bold uppercase tracking-[0.25em] text-[#0e0e11] shadow-[0_0_30px_-6px_#ac8aff] hover:from-[#bfa5ff] hover:to-[#ac8aff] disabled:opacity-80"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              {signingIn ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening Google…
                </>
              ) : (
                <>
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="#0e0e11"
                      d="M21.35 11.1H12v3.2h5.35c-.23 1.45-1.7 4.25-5.35 4.25-3.22 0-5.85-2.66-5.85-5.95s2.63-5.95 5.85-5.95c1.83 0 3.06.78 3.76 1.45l2.56-2.46C16.7 4.16 14.55 3.2 12 3.2 6.96 3.2 2.9 7.26 2.9 12.3s4.06 9.1 9.1 9.1c5.25 0 8.73-3.69 8.73-8.88 0-.6-.07-1.06-.18-1.42z"
                    />
                  </svg>
                  Continue with Google
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
            {isNativeApp() && (
              <p className="mt-3 text-center text-[10px] text-[#76757b]">
                Pick your Google account — you'll stay inside the app.
              </p>
            )}

            {/* Email auth hidden — Google sign-in only.
                Backend still accepts email logins for existing users. */}

            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-white/10" />
              <span
                className="text-[9px] font-bold uppercase tracking-[0.3em] text-[#76757b]"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                Why FitAura
              </span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <ul className="space-y-2.5 text-xs text-[#acaab1]">
              {[
                { icon: Zap, t: "Instant access — no password to remember" },
                { icon: Lock, t: "Encrypted bio-data, never shared or trained on" },
                { icon: ShieldCheck, t: "1 free AI try-on on signup" },
              ].map(({ icon: Icon, t }) => (
                <li key={t} className="flex items-center gap-2.5">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#ac8aff]/10 text-[#ac8aff]">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-[#76757b]"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            By continuing, you agree to our{" "}
            <Link to="/terms" className="underline transition-colors hover:text-[#bfa5ff]">
              Terms
            </Link>
            ,{" "}
            <Link to="/privacy" className="underline transition-colors hover:text-[#bfa5ff]">
              Privacy
            </Link>{" "}
            &{" "}
            <Link to="/refund" className="underline transition-colors hover:text-[#bfa5ff]">
              Refund
            </Link>
          </motion.p>
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/5 px-5 py-5 text-center text-[9px] font-semibold uppercase tracking-[0.25em] text-[#76757b] md:px-8">
        <span style={{ fontFamily: HEADLINE_FONT }}>
          © {new Date().getFullYear()} FitAura AI Labs · Built for the high-velocity aesthetic
        </span>
      </footer>
    </div>
    </>
  );
};

export default Auth;
